import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import colours from 'styles/_colours.module.scss';
import styles from 'styles/variables-for-js.module.scss';
import ChartTooltip from './chart-tooltip';
import { TraitChartProps } from './trait-chart-props';
import {
  animalPercentiles, chartOptions, flipPercentile, getColour, getPercentiles,
} from './trait-chart-utils';
import './trait-chart.scss';

export const TraitChart: React.FC<TraitChartProps> = memo((props) => {
  const {
    asbvs: animals,
  } = props;
  const { traitsChartHeight } = styles;
  const chartRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const [chartWidth, setChartWidth] = useState<number>();

  const [seriesCollection, setSeriesCollection] = useState<animalPercentiles[]>([]);
  const [chartStateSeriesCollection, setChartStateSeriesCollection] = useState<any[]>([]);

  /**
   * Returns tooltip component.
   * @param dataTrigger marker or column that triggered the tooltip
   */
  const createTooltip = (dataTrigger: any) => {
    const { dataPointIndex } = dataTrigger;
    const { seriesIndex } = dataTrigger;
    const animalPercentile = seriesCollection[seriesIndex]?.markerSeries[dataPointIndex];

    return renderToString(<ChartTooltip
      percentile={animalPercentile}
      nonoptimal={!!seriesCollection[seriesIndex]?.barSeries[dataPointIndex]}
    />);
  };

  /**
   * Adds a stroke to the chart circles.
   */
  const addCircleStroke = useCallback(() => {
    if (chartRef && chartRef.current && seriesCollection) {
      const chart: any = chartRef.current;
      const chartSVGref = chart.chartRef?.current;
      const getCircles: HTMLCollection = chartSVGref?.getElementsByTagName('circle');

      setChartWidth(chartSVGref.offsetWidth);

      // creates array of colours depend on percentile value
      const col: Array<any> = [];
      seriesCollection.forEach((currentSeries: any) => currentSeries.markerSeries.forEach((percentile: any) => col.push(getColour(percentile))));

      Array.from(getCircles).forEach((circle: any, i: any) => {
        const el = document.getElementById(circle.id);
        // checks if there is a value for a bar and if it is,
        // then colours the marker depend on value
        //
        // eslint marks this as an unused expression which it technically is, since
        // we're not using the result. But calling the setter function does produce a
        // change so this is fine.
        //
        // eslint-disable-next-line no-unused-expressions
        seriesCollection.forEach((currentSeries: any) => el?.setAttribute('style', currentSeries.barSeries[i]
          ? `stroke: ${col[i]}`
          : `stroke: ${colours.grey}`));
      });

      // hide last marker if it's null, fix for apexcharts bug when series has only null values
      const lastSeries = seriesCollection[seriesCollection.length - 1];
      const lastMarker = lastSeries?.markerSeries[lastSeries.markerSeries.length - 1];
      if (lastMarker === null) {
        getCircles[getCircles.length - 1].setAttribute('style', 'display: none');
      }
    }
  }, [seriesCollection]);

  useEffect(() => {
    let categoryNames: Array<string> = [];
    let newSeriesCollection: Array<animalPercentiles> = [];
    let newChartStateSeriesCollection: Array<any> = [];

    animals.forEach((animal) => {
      categoryNames = categoryNames.concat(animal.asbvs.map((a) => a.name).filter((n) => !categoryNames.includes(n)));
      const s = getPercentiles(animal.asbvs);
      newSeriesCollection = newSeriesCollection.concat(s);

      const stateSeries = [
        {
          name: 'animal asbvs markers',
          type: 'line',
          data: s.markerSeries.map((e: number) => flipPercentile(e)),
        },
        {
          name: 'animal asbvs bars',
          type: 'bar',
          data: s.barSeries.map((e: number) => flipPercentile(e)),
        },
      ];
      newChartStateSeriesCollection = newChartStateSeriesCollection.concat(stateSeries);
    });

    setCategories(categoryNames.filter((v, i, a) => a.indexOf(v) === i));
    setSeriesCollection(newSeriesCollection);
    setChartStateSeriesCollection(newChartStateSeriesCollection);
    setReady(true);
  }, [animals]);

  useEffect(() => {
    if (ready) {
      addCircleStroke();
    }
  }, [addCircleStroke, ready]);

  /**
   * Adds the circle strokes when the container subtree changes.
   *
   * This is required due to the Chart component recreating if a window size changes.
   */
  useEffect(() => {
    const observer = new MutationObserver(() => {
      addCircleStroke();
    });
    observer.observe(containerRef.current as HTMLDivElement, { attributes: false, childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [addCircleStroke]);

  return (
    <div
      className="sg-trait-chart"
      ref={containerRef}
    >
      {ready && (
        <Chart
          options={chartOptions(props.id, categories, props.yAxisLevels, createTooltip, chartWidth)}
          series={chartStateSeriesCollection}
          type="line"
          height={parseInt(traitsChartHeight)}
          ref={chartRef}
        />
      )}
    </div>
  );
});

// (TraitChart as any).whyDidYouRender = {
//   customName: 'TraitChart',
// };
